import React, { useState, useEffect, useRef } from 'react';
import type * as CSS from 'csstype';

interface MagnifierType {
  /** 缩略图(缩略图和放大图的宽高比需保持一致) */
  thumb: string;
  /** 放大图(原图)(缩略图和放大图的宽高比需保持一致) */
  original: string;
  /** 放大倍数，默认4倍 */
  scale?: number;
  /** 放大镜显示位置，固定在容器角落 | 随鼠标移动 */
  magnifierPosition?: 'fixed' | 'cursor';
  /** 放大镜容器边长，默认200px */
  magnifierContainerLength?: number;
  /** 鼠标悬停小方块边长，默认50px(magnifierContainerLength / scale = blockLength) */
  blockLength?: number;
  /** 是否显示悬停小方块 */
  showBlock?: boolean;
}

/** 带放大镜的图片，object-fit: contain; */
const ImageMagnifier = ({
  thumb,
  original,
  scale = 4,
  magnifierPosition = 'cursor',
  magnifierContainerLength = 200,
  blockLength = 50,
  showBlock = false,
}: MagnifierType) => {
  const thumbRef = useRef(null);

  const [thumbImg, setThumbImg] = useState('');
  const [originalImg, setOriginalImg] = useState('');
  const [magnifierOff, setMagnifierOff] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false); // 原图片加载状态
  const [magnifierImgLoad, setMagnifierImgLoad] = useState(false); // 放大镜框内图片加载状态
  const [cssStyle, setCssStyle] = useState({
    // 鼠标悬停遮罩层样式
    mouseMaskStyle: {
      width: '100%',
      height: '100%',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    },
    // 鼠标悬停小方块样式
    mouseBlock: {
      top: 0,
      left: 0,
      width: blockLength,
      height: blockLength,
    },
    //  放大镜容器样式
    magnifierContainer: {
      left: 0,
      top: 0,
    },
    // 图片放大样式
    magnifierImgStyle: {
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    },
  });

  // 组件初始化
  useEffect(() => {
    updataImg(thumb, original);
  }, [thumb, original]);

  // 鼠标移入
  const mouseEnter = () => {
    setMagnifierOff(true);
  };
  // 鼠标移除
  const mouseLeave = () => {
    setMagnifierOff(false);
  };
  // 鼠标移动
  const mouseMove = (event) => {
    // console.log(event);
    const e = event.nativeEvent;
    calculationBlock(e.offsetX, e.offsetY);
  };

  // 计算相关参数
  const calculationBlock = (offsetX: number, offsetY: number) => {
    // console.log(offsetX, offsetY);
    const width = thumbRef.current?.clientWidth || 0;
    const height = thumbRef.current?.clientHeight || 0;

    /* 小方块位置 */
    const realBlockWidth = Math.min(width, blockLength);
    const realBlockHeight = Math.min(height, blockLength);
    const halfBlockWidth = realBlockWidth / 2;
    const halfBlockHeight = realBlockHeight / 2;
    const maxBlockLeft = width - realBlockWidth;
    const maxBlockTop = height - realBlockHeight;
    let blockX = offsetX - halfBlockWidth;
    let blockY = offsetY - halfBlockHeight;
    // 防止鼠标移动过快导致计算失误，只要小于或者大于对应值，直接设置偏移量等于最小值或者最大值
    if (blockX < 0) {
      blockX = 0;
    }
    if (blockX > maxBlockLeft) {
      blockX = maxBlockLeft;
    }
    if (blockY < 0) {
      blockY = 0;
    }
    if (blockY > maxBlockTop) {
      blockY = maxBlockTop;
    }

    /* 放大镜位置 */
    let magContainerX = 0;
    let magContainerY = 0;
    const realMagContainerWidth = Math.min(width, magnifierContainerLength);
    const realMagContainerHeight = Math.min(height, magnifierContainerLength);
    if (magnifierPosition === 'fixed') {
      if (blockY < realMagContainerHeight) {
        magContainerY = height - realMagContainerHeight;
      } else {
        magContainerY = 0;
      }
    } else if (magnifierPosition === 'cursor') {
      const maxMagContainerLeft = width - realMagContainerWidth;
      const maxMagContainerTop = height - realMagContainerHeight;
      const halfMagContainerWidth = realMagContainerWidth / 2;
      const halfMagContainerHeight = realMagContainerHeight / 2;
      magContainerX = offsetX - halfMagContainerWidth;
      magContainerY = offsetY - halfMagContainerHeight;
      if (magContainerX < 0) {
        magContainerX = 0;
      }
      if (magContainerX > maxMagContainerLeft) {
        magContainerX = maxMagContainerLeft;
      }
      if (magContainerY < 0) {
        magContainerY = 0;
      }
      if (magContainerY > maxMagContainerTop) {
        magContainerY = maxMagContainerTop;
      }
    }

    /* 计算图片放大位置 */
    let magImgX = 0;
    let magImgY = 0;
    if (magnifierPosition === 'cursor') {
      magImgX = -blockX * scale;
      magImgY = -blockY * scale;
    }

    const newCssStyle = {
      ...cssStyle,
      mouseMaskStyle: {
        ...cssStyle.mouseMaskStyle,
        width,
        height,
      },
      mouseBlock: {
        ...cssStyle.mouseBlock,
        width: realBlockWidth,
        height: realBlockHeight,
        left: blockX,
        top: blockY,
      },
      magnifierContainer: {
        ...cssStyle.magnifierContainer,
        width: realMagContainerWidth,
        height: realMagContainerHeight,
        left: magContainerX,
        top: magContainerY,
      },
      magnifierImgStyle: {
        ...cssStyle.magnifierImgStyle,
        width: width * scale,
        height: height * scale,
        left: magImgX,
        top: magImgY,
      },
    };
    setCssStyle(newCssStyle);
  };

  // 更新图片
  const updataImg = (thumb: string, original: string) => {
    setThumbImg(thumb);
    setOriginalImg(original);
  };
  // 图片加载情况
  const handleImageLoaded = (e) => {
    // console.log(e);
    setMagnifierImgLoad(true);
  };

  // 图片加载中
  const handleImageErrored = () => {
    setMagnifierImgLoad(false);
  };
  return (
    <div className="w-full h-full max-w-full max-h-full relative flex justify-center items-center object-contain">
      <img
        ref={thumbRef}
        // 图片样式
        style={{
          objectFit: 'contain',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
        src={thumbImg}
        onLoad={() => setImgLoaded(true)}
        alt=""
      />
      {imgLoaded && ( // README: 防止原图片未加载出来时该div宽高就在mouseMove中被设置成0
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'rgba(0,0,0,0)',
            zIndex: 100,
            cursor: 'move',
            ...cssStyle.mouseMaskStyle,
          }}
          onMouseEnter={mouseEnter}
          onMouseLeave={mouseLeave}
          onMouseMove={mouseMove}
        />
      )}
      {magnifierOff && (
        <>
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: cssStyle.mouseMaskStyle.width,
              height: cssStyle.mouseMaskStyle.height,
            }}
          >
            <div className="relative w-full h-full">
              {showBlock && (
                <div
                  style={{
                    position: 'absolute',
                    background: 'rgba(0, 0, 0, 0.1)',
                    zIndex: 99,
                    ...cssStyle.mouseBlock,
                  }}
                />
              )}
              <div
                // 放大镜容器样式
                style={{
                  position: 'absolute',
                  overflow: 'hidden',
                  zIndex: 98,
                  border: '5px solid #fff',
                  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 3px 10px',
                  left: 0,
                  top: 0,
                  width: magnifierContainerLength,
                  height: magnifierContainerLength,
                  ...cssStyle.magnifierContainer,
                }}
              >
                <img
                  style={{
                    position: 'absolute',
                    maxWidth: 'none',
                    // 此处图片宽高不能设置为百分比，在scale的作用下，放大的只是图片初始的宽高 ！！！
                    // transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    ...cssStyle.magnifierImgStyle,
                  }}
                  src={originalImg}
                  onLoad={handleImageLoaded}
                  onError={handleImageErrored}
                  alt=""
                />
                {!magnifierImgLoad && 'failed to load'}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ImageMagnifier;
